import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { getAssay, type Assay, type AssayItem } from './api'
import {
  CategoryCoverage, FileList, ItemPanel, LatencyProfile, Section, SourceList, StandingPanel,
  Stat,
} from './assay/rails'
import { Plate, keyOf } from './assay/plate'
import { CREDIBILITY, RELIABILITY, fmtLatency, pct, share, timeDomain } from './assay/scale'
import {
  MARGIN, drawChronology, drawDensity, hitTest, placeItems, readTheme, timeAt,
  type ChronoScene, type Placement, type Theme,
} from './assay/chronology'

/**
 * The Assay — the dossier's landing board.
 *
 * ## Why this is a plate and not a map
 *
 * The war room's console is a map because its question is about spread: what is being said,
 * where, and how much. A dossier's question is not about spread at all. It is
 * <em>what does this record rest on, and would it survive being asked?</em> — a question about
 * quality, provenance and lateness, none of which has a geography. So this board is laid out
 * as a plate from a paper: numbered figures, hairline rules, a stated denominator under every
 * proportion, and one heavy line running through all of it.
 *
 * That line is the brief floor. It appears twice — as the staircase around the quotable region
 * of the Admiralty plate, and as the datum across the chronology — and both come from one
 * server-computed number, so the two figures cannot disagree about where it sits.
 *
 * ## Brushing dims; it never re-counts
 *
 * Every control on this screen except the window and the floor is a BRUSH. Picking a grade, a
 * source, a file or a span of time fades everything it did not match and leaves the figures
 * describing the whole window. That is the opposite of a filter, and it is deliberate: a plate
 * that re-aggregated on every click would let a reader select their way to a flattering
 * picture and never see that they had. The window and the floor DO re-read, because they
 * change what is being measured rather than what is being looked at — and both are stated in
 * the footer.
 *
 * ## It does not poll
 *
 * A record changes when somebody files something or a collector runs, not on a timer, and a
 * board that quietly re-read itself under an analyst mid-brush would move the thing they were
 * pointing at. The generation time is on the footer and re-reading is a button.
 */
export function AssayPage() {
  const navigate = useNavigate()
  const [assay, setAssay] = useState<Assay | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // ── what is being measured (these re-read) ─────────────────────────────────
  const [windowDays, setWindowDays] = useState(180)
  /*
     The board opens SIMPLE, and that is the default on purpose.

     The three figures below are the correct instruments and they are also unreadable to
     everybody except the analyst who commissioned them. A screen that only its author can
     read gets skipped, and the decision then gets made without it — so the plain reading is
     what loads, and the plate is one click away for the reader who wants it.
  */
  const [expert, setExpert] = useState(false)
  const [floorRel, setFloorRel] = useState('C')
  const [floorCred, setFloorCred] = useState('3')

  // ── what is being looked at (these only dim) ───────────────────────────────
  const [grades, setGrades] = useState<Set<string>>(() => new Set())
  const [subjectId, setSubjectId] = useState<string | null>(null)
  const [sourceId, setSourceId] = useState<string | null>(null)
  const [categoryCode, setCategoryCode] = useState<string | null>(null)
  const [range, setRange] = useState<{ from: number, to: number } | null>(null)
  const [query, setQuery] = useState('')

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [showLigatures, setShowLigatures] = useState(true)
  const [showLinks, setShowLinks] = useState(true)
  const [railsOpen, setRailsOpen] = useState(true)

  const plateRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const brushRef = useRef<HTMLCanvasElement>(null)
  const themeRef = useRef<Theme | null>(null)
  const placesRef = useRef<Placement[]>([])
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1 })

  const load = useCallback(async () => {
    setBusy(true)
    try {
      setError(null)
      setAssay(await getAssay({
        windowDays,
        floorReliability: floorRel,
        floorCredibility: floorCred,
      }))
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }, [windowDays, floorRel, floorCred])

  useEffect(() => { void load() }, [load])

  // ── the brush ──────────────────────────────────────────────────────────────

  /**
   * Ids the brush did NOT match.
   *
   * Null and the empty set are different on purpose. Null means nothing is brushing, so
   * everything draws at full weight. An empty set would mean "the brush matched everything",
   * which is a different statement and would still print the chips explaining what was
   * selected.
   */
  const missed = useMemo(() => {
    if (!assay) return null
    const q = query.trim().toLowerCase()
    const brushing = grades.size > 0 || subjectId || sourceId || categoryCode || range || q
    if (!brushing) return null
    const out = new Set<string>()
    for (const item of assay.items) {
      const at = Date.parse(item.collectedAt)
      const hay = `${item.title ?? ''} ${item.subjectName} ${item.sourceName}`.toLowerCase()
      const keep =
        (grades.size === 0 || grades.has(keyOf(item)))
        && (!subjectId || item.subjectId === subjectId)
        && (!sourceId || item.sourceId === sourceId)
        && (!categoryCode || item.categoryCode === categoryCode)
        && (!range || (at >= range.from && at <= range.to))
        && (!q || hay.includes(q))
      if (!keep) out.add(item.id)
    }
    return out
  }, [assay, grades, subjectId, sourceId, categoryCode, range, query])

  const shown = assay ? assay.items.length - (missed?.size ?? 0) : 0

  const clearBrush = useCallback(() => {
    setGrades(new Set())
    setSubjectId(null)
    setSourceId(null)
    setCategoryCode(null)
    setRange(null)
    setQuery('')
  }, [])

  const toggleGrades = useCallback((keys: string[]) => {
    setGrades((prev) => {
      const next = new Set(prev)
      // A click on a row or column header adds the whole run unless the run is already fully
      // selected, in which case it clears it — the same "select, then unselect" the single
      // cell gives, scaled up. Anything else makes the header a control that can only add.
      const allOn = keys.every((k) => next.has(k))
      for (const key of keys) {
        if (allOn) next.delete(key)
        else next.add(key)
      }
      return next
    })
  }, [])

  // ── the canvas ─────────────────────────────────────────────────────────────

  const domain = useMemo(
    () => timeDomain(assay?.items ?? [], assay?.windowDays ?? windowDays),
    [assay, windowDays],
  )

  const sceneOf = useCallback((): ChronoScene | null => {
    const theme = themeRef.current
    if (!assay || !theme) return null
    return {
      items: assay.items,
      links: assay.links,
      floor: assay.floor,
      domain,
      windowFrom: Date.now() - assay.windowDays * 86_400_000,
      width: sizeRef.current.w,
      height: sizeRef.current.h,
      dpr: sizeRef.current.dpr,
      theme,
      missed,
      hovered: hoveredId,
      selected: selectedId,
      showLigatures,
      showLinks,
    }
  }, [assay, domain, missed, hoveredId, selectedId, showLigatures, showLinks])

  /**
   * Draw once, now.
   *
   * There is no animation loop. Every call site is a thing that actually changed the picture —
   * new data, a brush, a pointer, a resize — so the canvas is idle whenever the reader is, and
   * the figure holds still while somebody points at it.
   */
  const render = useCallback(() => {
    const canvas = canvasRef.current
    const scene = sceneOf()
    if (!canvas || !scene || scene.width < 40) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const places = placeItems(scene)
    placesRef.current = places
    drawChronology(ctx, scene, places)

    const brush = brushRef.current
    const bctx = brush?.getContext('2d')
    if (brush && bctx && assay) {
      drawDensity(bctx, assay.items, domain, scene.width, brush.clientHeight,
        scene.dpr, scene.theme, range)
    }
  }, [sceneOf, assay, domain, range])

  useEffect(() => {
    const stage = stageRef.current
    const canvas = canvasRef.current
    const brush = brushRef.current
    const plate = plateRef.current
    if (!stage || !canvas || !brush || !plate) return

    themeRef.current = readTheme(plate)

    const resize = () => {
      const rect = stage.getBoundingClientRect()
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const w = Math.max(40, rect.width)
      const h = Math.max(40, rect.height)
      sizeRef.current = { w, h, dpr }
      canvas.width = w * dpr
      canvas.height = h * dpr
      canvas.style.width = `${w}px`
      canvas.style.height = `${h}px`
      const bh = brush.clientHeight || 30
      brush.width = w * dpr
      brush.height = bh * dpr
      brush.style.width = `${w}px`
      render()
    }
    resize()
    const observer = new ResizeObserver(resize)
    observer.observe(stage)

    // The palette lives in CSS custom properties, so a theme change has to be re-read: the
    // rasterised numbers were resolved against the old ones and would otherwise leave the
    // figure drawn in yesterday's ink.
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onScheme = () => {
      themeRef.current = readTheme(plate)
      render()
    }
    media.addEventListener('change', onScheme)
    const themeWatch = new MutationObserver(onScheme)
    themeWatch.observe(document.documentElement, {
      attributes: true, attributeFilter: ['class'],
    })

    return () => {
      observer.disconnect()
      media.removeEventListener('change', onScheme)
      themeWatch.disconnect()
    }
  }, [render])

  useEffect(() => { render() }, [render])

  const onCanvasMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const hit = hitTest(placesRef.current, e.clientX - rect.left, e.clientY - rect.top)
    if (hit !== hoveredId) setHoveredId(hit)
  }

  const onCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setSelectedId(hitTest(placesRef.current, e.clientX - rect.left, e.clientY - rect.top))
  }

  const onCanvasDouble = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const hit = hitTest(placesRef.current, e.clientX - rect.left, e.clientY - rect.top)
    const item = assay?.items.find((i) => i.id === hit)
    if (item) navigate(`/dossier/${item.subjectId}`)
  }

  /**
   * The brush strip.
   *
   * A drag under 4px is treated as a click that CLEARS the span rather than as a
   * zero-width selection — otherwise a stray click on the strip silently hides the entire
   * chronology and looks like a crash.
   */
  const onBrushDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Without this the drag starts a text selection over the caption underneath, and the
    // reader gets a highlighted sentence instead of a span of time.
    e.preventDefault()
    const el = e.currentTarget
    const rect = el.getBoundingClientRect()
    const startX = e.clientX - rect.left
    const width = sizeRef.current.w
    let moved = 0

    const move = (ev: MouseEvent) => {
      const x = ev.clientX - rect.left
      moved = Math.max(moved, Math.abs(x - startX))
      if (moved < 4) return
      const a = timeAt(domain, width, Math.min(startX, x))
      const b = timeAt(domain, width, Math.max(startX, x))
      setRange({ from: a, to: b })
    }
    const up = () => {
      if (moved < 4) setRange(null)
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
    }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (selectedId) setSelectedId(null)
      else clearBrush()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedId, clearBrush])

  // ── render ─────────────────────────────────────────────────────────────────

  if (error && !assay) return <ErrorStrip message={error} />
  if (!assay) return <LoadingBlock label="Reading the record" />

  const s = assay.standing
  const selected = assay.items.find((i) => i.id === selectedId) ?? null
  const hovered = assay.items.find((i) => i.id === hoveredId) ?? null
  const chips = brushChips({ assay, grades, subjectId, sourceId, categoryCode, range, query })

  return (
    <div ref={plateRef}
         className="axis-plate relative -mx-3 -mb-3 flex h-[calc(100dvh-9.5rem)] min-h-[600px]
                    flex-col overflow-hidden border"
         style={{ borderColor: 'var(--plate-rule)' }}>

      {/* ── masthead ── */}
      <header className="flex shrink-0 flex-wrap items-stretch border-b"
              style={{ borderColor: 'var(--plate-rule)' }}>
        <div className="flex items-center gap-3 border-r px-4 py-2.5"
             style={{ borderColor: 'var(--plate-rule)' }}>
          <Monogram />
          <span className="flex flex-col justify-center">
            <b className="font-serif text-[17px] leading-tight font-normal"
               style={{ color: 'var(--plate-ink)' }}>The Assay</b>
            <span className="axis-plate-eyebrow mt-px">Standing of the record</span>
          </span>
        </div>

        {/* The floor. Two ordered selects rather than a slider, because the pair is a
            conjunction of two DIFFERENT scales and a single control would imply one. */}
        <div className="flex items-center gap-2 border-r px-4"
             style={{ borderColor: 'var(--plate-rule)' }}>
          <span className="axis-plate-eyebrow whitespace-nowrap"
                title="Only items at least this good on both scales count as quotable">
            Usable from
          </span>
          <select value={floorRel} onChange={(e) => setFloorRel(e.target.value)}
                  aria-label="Floor: source reliability"
                  className="border bg-transparent px-1 py-1 font-mono text-[11px]"
                  style={{ borderColor: 'var(--plate-rule)', color: 'var(--plate-ink)' }}>
            {RELIABILITY.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <select value={floorCred} onChange={(e) => setFloorCred(e.target.value)}
                  aria-label="Floor: information credibility"
                  className="border bg-transparent px-1 py-1 font-mono text-[11px]"
                  style={{ borderColor: 'var(--plate-rule)', color: 'var(--plate-ink)' }}>
            {CREDIBILITY.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div className="flex items-center gap-px border-r px-2"
             style={{ borderColor: 'var(--plate-rule)' }}>
          {[30, 90, 180, 365, 3650].map((d) => (
            <button key={d} type="button" aria-pressed={windowDays === d}
                    onClick={() => setWindowDays(d)} className="axis-plate-toggle">
              {d === 3650 ? 'All' : d < 365 ? `${d}d` : `${d / 365}y`}
            </button>
          ))}
        </div>

        <label className="flex min-w-[180px] flex-1 items-center px-3">
          <span className="sr-only">Search the drawn sample by title, file or source</span>
          <input value={query} onChange={(e) => setQuery(e.target.value)}
                 placeholder="Search title, file or source"
                 className="w-full max-w-sm bg-transparent py-2 text-[11.5px] outline-none
                            placeholder:opacity-50"
                 style={{ color: 'var(--plate-ink)' }} />
        </label>

        <div className="flex items-center gap-6 border-l px-5"
             style={{ borderColor: 'var(--plate-rule)' }}>
          <Stat label="Items" value={s.items.toLocaleString()}
                note="Every item collected in the window" />
          {/* Plain labels, with the Admiralty term moved into the note. Both are true; only
              one of them answers the question the reader actually has, and a board nobody
              outside the research desk can read gets ignored — after which the decision is
              made without it, which is what this screen exists to prevent. */}
          <Stat label="Usable"
                value={s.items ? pct(share(s.atOrAboveFloor, s.items)) : '—'}
                tone={share(s.atOrAboveFloor, s.items) < 0.5 ? 'below' : 'above'}
                note={`Strong enough to quote — meets ${assay.floor.reliability}${assay.floor.credibility} on BOTH axes, source and claim`} />
          <Stat label="Typical delay" value={fmtLatency(s.latencyP50Minutes)}
                note="Half of the dated items took at least this long to reach the file" />
          <Stat label="Files on one source" value={`${s.singleSourceSubjects}/${s.subjects}`}
                tone={s.singleSourceSubjects > 0 ? 'below' : undefined}
                note="Their whole record comes from a single publication — unrepeated, not untrue" />
          <Stat label="Contested" value={s.disputed + s.debunked}
                tone={s.disputed + s.debunked > 0 ? 'dispute' : undefined}
                note="Disputed by somebody, or shown to be false" />
        </div>

        <div className="flex items-center gap-1 border-l px-2"
             style={{ borderColor: 'var(--plate-rule)' }}>
          <button type="button" onClick={() => void load()} disabled={busy}
                  className="axis-plate-toggle disabled:opacity-40"
                  title="This board does not poll — a record changes when somebody files
                         something, not on a timer">
            {busy ? 'Reading…' : 'Re-read'}
          </button>
          <button type="button" aria-pressed={!railsOpen}
                  onClick={() => setRailsOpen((was) => !was)} className="axis-plate-toggle"
                  title="Hide the rails and give the figures the full width">
            Figures only
          </button>
        </div>
      </header>

      {/* The plain reading sits above the apparatus that justifies it — the same decision as
          the Estimator. Everything below is correct and none of it is legible to somebody
          deciding whether a claim can go in a speech, which in politics is who reads it. */}
      <PlainBand assay={assay} expert={expert} onToggle={() => setExpert((v) => !v)} />


      {/* ── what the brush is holding ── */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b px-4 py-1.5"
           style={{ borderColor: 'var(--plate-rule)' }}>
        <span className="axis-plate-eyebrow">Selected</span>
        {chips.length === 0 ? (
          <span className="axis-plate-mini">
            Nothing selected — every figure is describing the whole window
          </span>
        ) : (
          <>
            {chips.map((chip) => (
              <span key={chip} className="axis-plate-tag">{chip}</span>
            ))}
            <span className="axis-plate-mini tabular-nums">
              {shown.toLocaleString()} of {assay.items.length.toLocaleString()} drawn ·
              the counts above are unchanged
            </span>
            <button type="button" onClick={clearBrush}
                    className="axis-plate-mini underline underline-offset-2">Clear</button>
          </>
        )}
      </div>

      {/* ── the plate ── */}
      <div className="grid min-h-0 flex-1"
           style={{
             gridTemplateColumns: railsOpen ? '306px 1fr 336px' : '1fr',
             gap: 1,
             background: 'var(--plate-rule)',
           }}>

        {railsOpen ? (
          <aside className="axis-plate-rail min-h-0 overflow-y-auto">
            {/* Each table is capped and scrolls inside itself, so Coverage — the only
                section that reports what is MISSING — stays reachable however many files or
                publications the register has grown to. */}
            <Section title="Files" note={`${assay.subjects.length}`}
                     storageKey="files" maxBodyHeight={236}>
              <FileList subjects={assay.subjects} selectedId={subjectId} onPick={setSubjectId} />
            </Section>
            <Section title="Publications" note={`${assay.sources.length}`}
                     storageKey="publications" maxBodyHeight={236}>
              <SourceList sources={assay.sources} selectedId={sourceId} onPick={setSourceId} />
            </Section>
            <Section title="Coverage"
                     note={`${assay.categories.filter((c) => c.items === 0).length} gaps`}
                     storageKey="coverage" maxBodyHeight={260}>
              <CategoryCoverage categories={assay.categories} selectedCode={categoryCode}
                                onPick={setCategoryCode} />
            </Section>
          </aside>
        ) : null}

        <div className="grid min-h-0 min-w-0"
             style={{ gridTemplateRows: 'auto 1fr', gap: 1, background: 'var(--plate-rule)' }}>

          {expert ? (
            <div className="grid gap-6 px-4 py-3.5"
                 style={{
                   background: 'var(--plate-paper)',
                   gridTemplateColumns: 'minmax(260px, 340px) minmax(240px, 1fr)',
                 }}>
              <Plate cells={assay.plate} floor={assay.floor} selected={grades}
                     onToggle={toggleGrades} onClear={() => setGrades(new Set())} />
              <LatencyProfile buckets={assay.latency} p50={s.latencyP50Minutes}
                              p90={s.latencyP90Minutes} />
            </div>
          ) : (
            <SimpleFigures assay={assay} />
          )}

          <div className={`relative min-h-0 min-w-0 flex-col ${expert ? 'flex' : 'hidden'}`}
               style={{ background: 'var(--plate-paper)' }}>
            <div className="flex shrink-0 items-baseline justify-between gap-3 px-4 pt-3">
              <span className="axis-plate-eyebrow">
                The chronology
              </span>
              <span className="flex items-center gap-2">
                <button type="button" aria-pressed={showLigatures}
                        onClick={() => setShowLigatures((v) => !v)}
                        className="axis-plate-toggle">Ligatures</button>
                <button type="button" aria-pressed={showLinks}
                        onClick={() => setShowLinks((v) => !v)}
                        className="axis-plate-toggle">Links</button>
                <span className="axis-plate-mini">
                  ● happened &nbsp; ○ we took custody &nbsp; ── the delay between them
                </span>
              </span>
            </div>

            <div ref={stageRef} className="relative min-h-0 min-w-0 flex-1">
              {/* A canvas is opaque to a screen reader, so the figure states its own reading.
                  Not decorative: this is the one place the custody delay is quantified, and a
                  reader who cannot see it still has to be told what it says. */}
              <canvas ref={canvasRef} className="block size-full"
                      role="img"
                      aria-label={`Chronology of ${assay.items.length} items. `
                        + `${pct(share(s.atOrAboveFloor, s.items))} sit at or above the `
                        + `${assay.floor.reliability}${assay.floor.credibility} floor. `
                        + `Half reached the file within ${fmtLatency(s.latencyP50Minutes)}; `
                        + `the slowest one in ten took ${fmtLatency(s.latencyP90Minutes)}. `
                        + `${s.undated} items have no event date.`}
                      onMouseMove={onCanvasMove}
                      onMouseLeave={() => setHoveredId(null)}
                      onClick={onCanvasClick}
                      onDoubleClick={onCanvasDouble} />
              {hovered ? <Marker item={hovered} places={placesRef.current} /> : null}
            </div>

            <div className="shrink-0 px-0 pb-2 select-none">
              <canvas ref={brushRef} className="axis-plate-brush block h-[30px] w-full"
                      onMouseDown={onBrushDown} />
              <p className="axis-plate-mini px-4"
                 style={{ paddingLeft: MARGIN.left }}>
                Collections over time — drag to brush a span, click to release it
              </p>
            </div>
          </div>
        </div>

        {railsOpen ? (
          <aside className="axis-plate-rail min-h-0 overflow-y-auto">
            {selected
              ? <ItemPanel item={selected} floor={assay.floor}
                           onOpenFile={(id) => navigate(`/dossier/${id}`)}
                           onClear={() => setSelectedId(null)} />
              : <StandingPanel assay={assay} />}
          </aside>
        ) : null}
      </div>

      {/* ── colophon ── */}
      <footer className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-1 border-t px-4 py-2"
              style={{ borderColor: 'var(--plate-rule)' }}>
        <span className="axis-plate-mini">
          Read {new Date(assay.generatedAt).toLocaleString()} · this board does not refresh itself
        </span>
        <span className="axis-plate-mini">
          Window {assay.windowDays >= 3650 ? 'all custody' : `${assay.windowDays} days`} on
          custody date
        </span>
        <span className="axis-plate-mini">
          Floor {assay.floor.reliability}{assay.floor.credibility}
        </span>
        {/* A sample is never labelled as a population. */}
        <span className="axis-plate-mini"
              style={{ color: assay.truncated ? 'var(--plate-below)' : undefined }}>
          {assay.truncated
            ? `Drawing every ${ordinal(assay.stride)} of ${assay.itemTotal.toLocaleString()} `
              + 'items, evenly across the window — every count above is over all of them'
            : `Drawing all ${assay.itemTotal.toLocaleString()} items in the window`}
        </span>
        {error ? (
          <span className="axis-plate-mini" style={{ color: 'var(--plate-dispute)' }}>
            Re-read failed · {error}
          </span>
        ) : null}
      </footer>
    </div>
  )
}

/**
 * The hover readout.
 *
 * Positioned in the DOM rather than painted into the canvas so it inherits the app's type and
 * can be read by a screen reader — and so a long title wraps instead of being clipped by a
 * measurement the renderer had to guess.
 */
function Marker({ item, places }: { item: AssayItem, places: Placement[] }) {
  const place = places.find((p) => p.id === item.id)
  if (!place) return null
  return (
    <div
      className="pointer-events-none absolute z-10 max-w-[280px] min-w-[190px] border px-2.5 py-2"
      style={{
        left: Math.max(4, Math.min(place.cx, 10_000)),
        top: place.y,
        transform: 'translate(-50%, -118%)',
        borderColor: 'var(--plate-rule-strong)',
        background: 'var(--plate-paper)',
      }}
    >
      <b className="mb-1 block text-[11.5px] leading-snug font-medium"
         style={{ color: 'var(--plate-ink)' }}>
        {item.title ?? 'Untitled observation'}
      </b>
      <span className="axis-plate-mini block">
        {item.reliability}{item.credibility} · {item.sourceName}
      </span>
      <span className="mt-1 block text-[10.5px]" style={{ color: 'var(--plate-ink-dim)' }}>
        {item.occurredAt
          ? `Happened ${new Date(item.occurredAt).toLocaleDateString()}, in the file after `
            + fmtLatency(item.latencyMinutes).toLowerCase()
          : 'No event date — the delay cannot be measured'}
      </span>
    </div>
  )
}

/** The brush, in words. A chip nobody can read is a filter nobody can undo. */
function brushChips({ assay, grades, subjectId, sourceId, categoryCode, range, query }: {
  assay: Assay
  grades: Set<string>
  subjectId: string | null
  sourceId: string | null
  categoryCode: string | null
  range: { from: number, to: number } | null
  query: string
}): string[] {
  const out: string[] = []
  if (grades.size > 0) {
    out.push(grades.size <= 4
      ? [...grades].sort().join(' ')
      : `${grades.size} grades`)
  }
  if (subjectId) {
    out.push(assay.subjects.find((s) => s.id === subjectId)?.displayName ?? 'one file')
  }
  if (sourceId) {
    out.push(assay.sources.find((s) => s.id === sourceId)?.name ?? 'one source')
  }
  if (categoryCode) {
    out.push(assay.categories.find((c) => c.code === categoryCode)?.label ?? categoryCode)
  }
  if (range) {
    out.push(`${new Date(range.from).toLocaleDateString()} – `
      + new Date(range.to).toLocaleDateString())
  }
  if (query.trim()) out.push(`“${query.trim()}”`)
  return out
}

const ordinal = (n: number) =>
  n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`

/**
 * The plate's mark.
 *
 * A crucible and a rule — an assay mark, drawn rather than imported so it takes the ink token
 * and needs no asset. The war room has a seal because it is an operations room; this has a
 * maker's mark because it is a document.
 */
/**
 * The record, in four sentences.
 *
 * A translation rather than a simplification: undated stays its own category instead of
 * folding into "fast", single-source stays "nobody has repeated it" instead of becoming
 * "doubtful", and the floor is named as a conjunction rather than hidden. The plate below is
 * untouched — this does not replace it, it makes it approachable, and the figures stay on the
 * page precisely so the plain claim can be checked.
 */
function PlainBand({ assay, expert, onToggle }: {
  assay: Assay; expert: boolean; onToggle: () => void
}) {
  const s = assay.standing
  const solid = s.items > 0 ? Math.round((s.atOrAboveFloor / s.items) * 100) : 0
  const verdict = s.items === 0
    ? 'There is nothing in this window to judge.'
    : solid >= 70 ? 'Most of this record would survive being challenged.'
    : solid >= 40 ? 'Some of this record would survive being challenged, and a lot of it would not.'
    : 'Very little of this record would survive being challenged.'

  return (
    <section className="shrink-0 border-b px-4 py-2.5"
             style={{ borderColor: 'var(--plate-rule)' }}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="axis-plate-eyebrow">In plain English</span>
        <button type="button" onClick={onToggle} aria-pressed={expert}
                className="axis-plate-toggle">
          {expert ? 'Show the simple view' : 'Show the analyst’s plate'}
        </button>
      </div>
      <p className="mt-0.5 text-[14px] font-medium" style={{ color: 'var(--plate-ink)' }}>
        {verdict}
      </p>
      <p className="mt-1 text-[11.5px] leading-relaxed" style={{ color: 'var(--plate-ink-dim)' }}>
        <b>{solid}%</b> of the {s.items.toLocaleString()} items here are graded well enough to put
        in front of a journalist or a court — which needs the source to be trusted <i>and</i> the
        specific claim to be judged credible, not one or the other.{' '}
        <b>{s.singleSource.toLocaleString()}</b> rest on a single source, which does not make them
        wrong, only unrepeated; the fix is to go and find the second source.{' '}
        {s.latencyP50Minutes > 0 ? (
          <>Half of what we hold reached us within <b>{fmtLatency(s.latencyP50Minutes)}</b> of the
            event.{' '}</>
        ) : null}
        {s.undated > 0 ? (
          <>{s.undated.toLocaleString()} items carry no event date, so they cannot be placed on a
            timeline — which is not the same as arriving instantly.{' '}</>
        ) : null}
        {s.debunked > 0 ? (
          <>{s.debunked} have been shown to be false and must never be quoted; they are kept
            rather than deleted so the correction stays on the record.</>
        ) : null}
      </p>
    </section>
  )
}

/**
 * The same three questions the plate answers, asked one at a time.
 *
 * ## Why the 6×6 grid is not the default any more
 *
 * The Admiralty plate is a JOINT distribution over two ordered axes with a staircase cut
 * through it, and reading it requires knowing what both axes mean, that the floor is a
 * conjunction, and that the diagonal is a trap. That is three pieces of prior knowledge
 * before the first number, and a campaign's research lead has none of them.
 *
 * The joint view is the right instrument for judging the SHAPE of a record's quality, so it
 * is still here — one click away. What it is bad at is answering "can we use this", and that
 * is the only question most readers arrive with. So the default splits the same data into
 * three ordinary bar charts, each with a question for a title:
 *
 *  1. Can we use it — one bar, three parts, plain words.
 *  2. Is the source trusted — the reliability axis alone, as a ranked list.
 *  3. Is the claim credible — the credibility axis alone, likewise.
 *
 * The margins are computed from the SAME `plate` cells the grid draws, so the two views can
 * never disagree, and the caption says explicitly that clearing one axis is not enough —
 * which is the one thing the split risks hiding and the grid made obvious.
 */
function SimpleFigures({ assay }: { assay: Assay }) {
  const s = assay.standing
  const total = s.items || 1

  // Margins off the joint distribution. `meetsFloor` is the server's own conjunction, so
  // "usable" here is never recomputed from the two letters — that is where a diagonal
  // would creep back in.
  const byReliability = new Map<string, { items: number; usable: number }>()
  const byCredibility = new Map<string, { items: number; usable: number }>()
  for (const c of assay.plate) {
    const r = byReliability.get(c.reliability) ?? { items: 0, usable: 0 }
    r.items += c.items; if (c.meetsFloor) r.usable += c.items
    byReliability.set(c.reliability, r)
    const k = byCredibility.get(c.credibility) ?? { items: 0, usable: 0 }
    k.items += c.items; if (c.meetsFloor) k.usable += c.items
    byCredibility.set(c.credibility, k)
  }

  const RELIABILITY: Record<string, string> = {
    A: 'A · Always reliable', B: 'B · Usually reliable', C: 'C · Fairly reliable',
    D: 'D · Not usually reliable', E: 'E · Unreliable', F: 'F · Cannot be judged',
  }
  const CREDIBILITY: Record<string, string> = {
    '1': '1 · Confirmed elsewhere', '2': '2 · Probably true', '3': '3 · Possibly true',
    '4': '4 · Doubtful', '5': '5 · Improbable', '6': '6 · Cannot be judged',
  }

  const contested = s.disputed + s.debunked
  const weak = Math.max(s.belowFloor - contested, 0)
  const parts = [
    { label: 'Strong enough to quote', value: s.atOrAboveFloor, colour: 'var(--plate-above)' },
    { label: 'Background only', value: weak, colour: 'var(--plate-below)' },
    { label: 'Disputed or debunked', value: contested, colour: 'var(--plate-dispute)' },
  ].filter((p) => p.value > 0)

  return (
    <div className="grid gap-6 overflow-y-auto px-4 py-3.5"
         style={{ background: 'var(--plate-paper)',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
      <div>
        <span className="axis-plate-eyebrow">Can we use it?</span>
        <div className="mt-2 flex h-6 w-full overflow-hidden rounded-sm" role="img"
             aria-label={parts.map((p) => `${p.label}: ${p.value}`).join(', ')}>
          {parts.map((p, i) => (
            <div key={p.label} className="flex h-full"
                 style={{ width: `${(p.value / total) * 100}%` }}>
              {i > 0 && <div className="w-0.5 shrink-0"
                             style={{ background: 'var(--plate-paper)' }} />}
              <div className="h-full flex-1" style={{ background: p.colour }}
                   title={`${p.label}: ${p.value}`} />
            </div>
          ))}
        </div>
        <ul className="mt-2 space-y-1">
          {parts.map((p) => (
            <li key={p.label} className="flex items-center gap-2 text-[11.5px]">
              <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-sm"
                    style={{ background: p.colour }} />
              <span className="flex-1" style={{ color: 'var(--plate-ink)' }}>{p.label}</span>
              <span className="tabular-nums" style={{ color: 'var(--plate-ink-dim)' }}>
                {p.value.toLocaleString()} · {Math.round((p.value / total) * 100)}%
              </span>
            </li>
          ))}
        </ul>
        <p className="axis-plate-mini mt-2">
          An item is usable only if it passes BOTH tests on the right. A perfect source making
          a claim nobody can check does not qualify, and neither does a solid claim from a
          source we do not trust.
        </p>
      </div>

      <AxisBars
        title="Is the source trusted?"
        note="How this platform grades the publication the item came from."
        rows={['A', 'B', 'C', 'D', 'E', 'F']
          .map((k) => ({ key: k, label: RELIABILITY[k]!, ...(byReliability.get(k) ?? { items: 0, usable: 0 }) }))}
      />

      <AxisBars
        title="Is the claim credible?"
        note="How the specific claim was judged, independently of who said it."
        rows={['1', '2', '3', '4', '5', '6']
          .map((k) => ({ key: k, label: CREDIBILITY[k]!, ...(byCredibility.get(k) ?? { items: 0, usable: 0 }) }))}
      />
    </div>
  )
}

/**
 * One ordered axis as a plain bar list.
 *
 * The full bar is everything at that grade and the darker inset is the part that is actually
 * usable — so a grade with a long bar and no inset reads immediately as "lots of this, none
 * of it quotable", which is the sentence the joint grid makes the reader derive.
 */
function AxisBars({ title, note, rows }: {
  title: string
  note: string
  rows: { key: string; label: string; items: number; usable: number }[]
}) {
  const max = Math.max(...rows.map((r) => r.items), 1)
  return (
    <div>
      <span className="axis-plate-eyebrow">{title}</span>
      <ul className="mt-2 space-y-1.5">
        {rows.map((r) => (
          <li key={r.key} className="grid grid-cols-[minmax(0,10.5rem)_1fr_auto] items-center gap-2">
            <span className="truncate text-[11.5px]" style={{ color: 'var(--plate-ink)' }}
                  title={r.label}>{r.label}</span>
            <span className="relative h-2.5 rounded-sm"
                  style={{ background: 'var(--plate-rule)' }}>
              <span className="absolute inset-y-0 left-0 rounded-sm"
                    style={{ width: `${(r.items / max) * 100}%`, background: 'var(--plate-below)' }} />
              <span className="absolute inset-y-0 left-0 rounded-sm"
                    style={{ width: `${(r.usable / max) * 100}%`, background: 'var(--plate-above)' }} />
            </span>
            <span className="w-14 text-right text-[11px] tabular-nums"
                  style={{ color: 'var(--plate-ink-dim)' }}>
              {r.items ? `${r.usable}/${r.items}` : '—'}
            </span>
          </li>
        ))}
      </ul>
      <p className="axis-plate-mini mt-2">
        {note} The solid part of each bar is what is usable once the other test is applied too;
        the count reads usable of total.
      </p>
    </div>
  )
}

function Monogram() {
  return (
    <svg viewBox="0 0 32 32" className="size-7 shrink-0" aria-hidden focusable="false"
         style={{ color: 'var(--plate-ink)' }} fill="none" stroke="currentColor">
      <rect x="3.5" y="3.5" width="25" height="25" strokeOpacity=".35" />
      <path d="M9 22h14" strokeWidth="1.4" />
      <path d="M9 22V10M23 22V10" strokeOpacity=".45" />
      <path d="M9 16h9M9 13h5" strokeOpacity=".8" />
      <path d="M14 19h9" strokeOpacity=".8" strokeDasharray="2 2" />
      <circle cx="9" cy="10" r="1.6" fill="currentColor" stroke="none" fillOpacity=".85" />
      <circle cx="23" cy="10" r="1.6" strokeOpacity=".85" />
    </svg>
  )
}
